import React, { useCallback } from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import type { ViewProps } from 'react-native';

import { theme, VStack } from '@sg/garnish';
import type { Address } from '@sg/graphql-schema';

import { AccountScreenBorderedSection } from '../Account';
import { AddressListItem } from './AddressListItem';

export type AddressListProps = Readonly<{
  items: readonly Address[];
  onSelectItem: (addressId: string) => void;
  onSelectAdd: () => void;
}> &
  ViewProps;

export const AddressList = ({
  items,
  onSelectItem,
  onSelectAdd,
}: AddressListProps): React.ReactElement => {
  const { formatMessage } = useIntl();

  const getTitle = useCallback(({ name, street }: Address): string => {
    if (name) return name;

    if (street) return street;

    return '';
  }, []);

  const getSubtitle = useCallback(
    ({ street, secondaryStreet, city, state, zipCode }: Address): string => {
      return [
        street,
        secondaryStreet,
        [city, [state, zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', '),
      ]
        .filter(Boolean)
        .join('\n');
    },
    [],
  );

  return (
    <AccountScreenBorderedSection>
      <VStack gap={0} hasDivider={true} dividerColor={theme.colors.LIGHT}>
        {items.map((address) => (
          <AddressListItem
            key={address.id}
            testID={`addresses.${address.name}-card-pressable`}
            accessibilityLabel={formatMessage(messages.addressesAddressLabel, {
              address: address.name,
            })}
            iconName={
              address.name
                ? address.name === 'home'
                  ? 'IconHomeStrokeSm'
                  : address.name === 'work'
                    ? 'IconWorkStrokeSm'
                    : 'IconCustomStrokeSm'
                : 'IconPickupStrokeSm'
            }
            title={getTitle(address)}
            subtitle={getSubtitle(address)}
            onPress={() => {
              onSelectItem(address.id);
            }}
          />
        ))}
        <AddressListItem
          testID="account.edit-address.add"
          accessibilityLabel={formatMessage(messages.addressesAddNew)}
          accessibilityHint={formatMessage(messages.addressesTitle)}
          iconName="IconAdd"
          title={formatMessage(messages.addressesAddNew)}
          onPress={onSelectAdd}
        />
      </VStack>
    </AccountScreenBorderedSection>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly addressesAddressLabel: { readonly address: MessageValue };
  readonly addressesAddNew: NoMessageValues;
  readonly addressesTitle: NoMessageValues;
}>({
  addressesAddressLabel: {
    defaultMessage: 'Select {address} address',
    description: 'Addresses | Address Label',
  },
  addressesAddNew: {
    defaultMessage: 'Add new address',
    description: 'Addresses | Add New',
  },
  addressesTitle: {
    defaultMessage: 'Addresses',
    description: 'Addresses | Title',
  },
});
